import {PropsWithChildren, useMemo, useState} from 'react';
import styled from 'styled-components';

import { Helpers } from 'utils';
import { Badge, Button, NoResult} from 'app/components';
import subscriptionService, { PlanType } from 'services/subscriptionService';
import { CancelModal } from 'app/components/CancelModal';
import { useAppDispatch, Actions } from 'store';
import AnalyticsEvents from 'services/analyticsEvents';
import { ClientSettings } from '../../../../services/settingsService';
import { useAppSelector } from '../../../../store';

interface Props {
    data: BrandSubscription | null;
    brand: BrandProfile;
    subscription: Subscription;
    subscriptions: Subscription[];
    hasSubscription?: boolean;
    hasActiveSubscription?: boolean;
    loadingSubscription?: boolean;
    onChangePlan?(): void;
    onChangeCard?(): void;
    onCancelPlan?(form: any): void;
}

export function SubscriptionInfo({
    data,
    subscription,
    subscriptions,
    hasSubscription,
    brand,
    hasActiveSubscription,
    loadingSubscription,
    children,
    onChangePlan,
    onChangeCard,
    onCancelPlan
}: PropsWithChildren<Props>) {
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showPauseModal, setShowPauseModal] = useState(false)
    const starterPlanMonthly = useMemo(() => subscriptionService.findSubscription(subscriptions, PlanType.STARTER, 'MONTH'), [subscriptions]);
    const starterFeatures = useMemo(() => subscriptionService.features(starterPlanMonthly), [starterPlanMonthly]);
    const growPlanMonthly = useMemo(() => subscriptionService.findSubscription(subscriptions, PlanType.GROW, 'MONTH'), [subscriptions]);
    const growFeatures = useMemo(() => subscriptionService.features(growPlanMonthly), [growPlanMonthly]);

    const isStarter = useMemo(() => subscriptionService.isSubscriptionType(subscription, PlanType.STARTER), [subscription]);
    const isGrow = useMemo(() => subscriptionService.isSubscriptionType(subscription, PlanType.GROW), [subscription]);
    const dispatcher = useAppDispatch();

    const {
        global: { settings },
    } = useAppSelector((state) => state);

    const analyticsEvents = useMemo(
        () => new AnalyticsEvents(settings as ClientSettings),
        [settings]
    );

    const toggleCancelModal = () => {
        const newState = !showCancelModal
        setShowCancelModal(newState)
        if (!newState && brand.uid) {
            analyticsEvents.subscriptionPlansCanceled({
                brand
            });
            dispatcher(Actions.Profile.getBrandProfile(brand?.uid)).catch((error: any) => {throw error})
        }
    }

    const togglePauseModal = () => {
        const newState = !showPauseModal
        setShowPauseModal(newState)
        if (!newState && brand.uid) {
            dispatcher(Actions.Profile.getBrandProfile(brand?.uid)).catch((error: any) => {throw error})
        }
    }

    const pauseSubscription = () => {
        if (brand.subscription) {
            analyticsEvents.subscriptionPlansPaused({
                brand
            });
            dispatcher(Actions.Profile.pauseSubscription(brand?.subscription)).catch((error: any) => {throw error})
            setShowPauseModal(false);
        }
    }


    const resumeSubscription = () => {
        if (brand.subscription) {
            analyticsEvents.subscriptionPlansResumed({
                brand
            });
            dispatcher(Actions.Profile.resumeSubscription(brand?.subscription)).catch((error: any) => {throw error})
        }
    }

    const displayStatus = useMemo(() => {
        switch (data?.status) {
            case 'active':
                if (data.canceled_at) {
                    return `canceled`;
                } else {
                    return 'active';
                }
            case 'incomplete_expired':
                return 'expired';
            case 'past_due':
            case 'unpaid':
                return 'last payment unsuccessful';
            case 'trialing':
                if (data.canceled_at) {
                    return `canceled`
                } else {
                    return 'trial';
                }
            case 'incomplete':
                return 'Not subscribed';
            default:
                return data?.status;
        }
    }, [data]);

    const status = useMemo(() => {
        switch (data?.status) {
            case 'active':
                if (data.canceled_at) {
                    return 'canceled';
                } else {
                    return 'active';
                }
            default:
                return data?.status;
        }
    }, [data]);

    return (
        <>
            <Container $loading={loadingSubscription && !subscription?.name}>

                {hasSubscription ?
                    (
                        <>
                            {children}

                            <Body>
                                <Header>
                                    <Title>
                                        {!loadingSubscription && subscription?.name}
                                    </Title>

                                    <Value>
                                        <span>$ {subscription?.amount}</span>

                                        <span>/ {subscription?.frequency}</span>
                                    </Value>
                                </Header>

                                <Group>
                                    <StatusBadge $status={status} className={`${displayStatus === 'canceled' ? 'canceled' : ''}`}>
                                        {displayStatus}
                                    </StatusBadge>
                                </Group>

                                <Divider />

                                <Details>
                                    <Group>
                                        <GroupTitle>
                                            This plan includes
                                        </GroupTitle>
                                        <Offers>
                                            {isStarter ? (
                                                <ul style={{listStyle: 'none'}}>
                                                    <Li><span>
                                                        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M31.5161 5.71048C39.0446 5.77498 45.2076 11.9045 45.3111 19.4325C45.3521 22.415 44.4541 25.1855 42.8946 27.468L43.9861 30.1255C44.0746 30.341 43.8806 30.566 43.6541 30.5095L40.8746 29.8165C38.3801 32.1385 35.0326 33.5565 31.3536 33.546C23.8716 33.5245 17.5416 27.2325 17.4771 19.751C17.4096 11.9675 23.7326 5.64398 31.5161 5.71048Z" fill="#FF855C"/>
                                                            <path d="M20.3648 13.4385C11.9073 13.4385 5.05078 20.2945 5.05078 28.7525C5.05078 31.9525 6.03278 34.9225 7.71078 37.3795L6.27478 40.8755L9.93328 39.9635C12.6678 42.509 16.3338 44.067 20.3648 44.067C28.8228 44.067 35.6788 37.2105 35.6788 28.753C35.6788 20.2955 28.8228 13.4385 20.3648 13.4385Z" fill="white"/>
                                                            <path d="M20.3649 44.7816C16.4299 44.7816 12.6709 43.3526 9.73594 40.7496L6.44794 41.5691C6.18644 41.6346 5.91044 41.5471 5.73394 41.3431C5.55744 41.1391 5.51094 40.8536 5.61344 40.6041L6.90494 37.4596C5.22294 34.8646 4.33594 31.8626 4.33594 28.7526C4.33594 19.9141 11.5264 12.7236 20.3649 12.7236C29.2034 12.7236 36.3939 19.9141 36.3939 28.7526C36.3944 37.5911 29.2039 44.7816 20.3649 44.7816ZM9.93394 39.2481C10.1124 39.2481 10.2874 39.3151 10.4209 39.4396C13.1309 41.9621 16.6624 43.3516 20.3649 43.3516C28.4149 43.3516 34.9639 36.8026 34.9639 28.7526C34.9639 20.7026 28.4149 14.1536 20.3649 14.1536C12.3149 14.1536 5.76594 20.7026 5.76594 28.7526C5.76594 31.7041 6.64294 34.5476 8.30144 36.9761C8.43744 37.1746 8.46394 37.4286 8.37244 37.6511L7.47344 39.8396L9.76094 39.2696C9.81794 39.2551 9.87594 39.2481 9.93394 39.2481Z" fill="#262262"/>
                                                            <path d="M14.9927 32.3893C14.5672 32.3893 14.2227 32.0448 14.2227 31.6193V25.9233C14.2227 25.4978 14.5672 25.1533 14.9927 25.1533C15.4182 25.1533 15.7627 25.4978 15.7627 25.9233V31.6193C15.7627 32.0448 15.4182 32.3893 14.9927 32.3893Z" fill="#262262"/>
                                                            <path d="M18.7192 32.3893C18.2937 32.3893 17.9492 32.0448 17.9492 31.6193V25.9233C17.9492 25.4978 18.2937 25.1533 18.7192 25.1533C19.1447 25.1533 19.4892 25.4978 19.4892 25.9233V31.6193C19.4892 32.0448 19.1442 32.3893 18.7192 32.3893Z" fill="#262262"/>
                                                            <path d="M21.6411 32.3893C21.2156 32.3893 20.8711 32.0448 20.8711 31.6193V25.9233C20.8711 25.4978 21.2156 25.1533 21.6411 25.1533C22.0666 25.1533 22.4111 25.4978 22.4111 25.9233V31.6193C22.4111 32.0448 22.0661 32.3893 21.6411 32.3893Z" fill="#262262"/>
                                                            <path d="M24.7856 29.9403C24.3601 29.9403 24.0156 29.5958 24.0156 29.1703V25.9233C24.0156 25.4978 24.3601 25.1533 24.7856 25.1533C25.2111 25.1533 25.5556 25.4978 25.5556 25.9233V29.1703C25.5556 29.5958 25.2106 29.9403 24.7856 29.9403Z" fill="#262262"/>
                                                            <path d="M24.7856 32.3537C24.3601 32.3537 24.0156 32.0092 24.0156 31.5837V31.5727C24.0156 31.1472 24.3601 30.8027 24.7856 30.8027C25.2111 30.8027 25.5556 31.1472 25.5556 31.5727V31.5837C25.5556 32.0092 25.2106 32.3537 24.7856 32.3537Z" fill="#262262"/>
                                                            <path d="M18.7172 29.4267H14.9927C14.5672 29.4267 14.2227 29.0822 14.2227 28.6567C14.2227 28.2312 14.5672 27.8867 14.9927 27.8867H18.7172C19.1427 27.8867 19.4872 28.2312 19.4872 28.6567C19.4872 29.0822 19.1422 29.4267 18.7172 29.4267Z" fill="#262262"/>
                                                        </svg>
                                                    </span> Create <strong>{starterFeatures.get('gifts.create.limit')} campaigns</strong> per month</Li>
                                                    <Li><span>
                                                            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M44.3862 11.8613H21.7312C20.3802 11.8613 19.2852 12.9563 19.2852 14.3073V31.1118C19.2852 32.4628 20.3802 33.5578 21.7312 33.5578H30.7842L33.7537 36.9973L36.8182 33.5578H44.3862C45.7372 33.5578 46.8322 32.4628 46.8322 31.1118V14.3073C46.8327 12.9563 45.7372 11.8613 44.3862 11.8613Z" fill="white"/>
                                                                <path d="M33.7541 37.7678C33.7521 37.7678 33.7501 37.7678 33.7481 37.7678C33.5266 37.7663 33.3161 37.6688 33.1711 37.5008L30.4321 34.3283H21.7316C19.9581 34.3283 18.5156 32.8853 18.5156 31.1123V14.3078C18.5156 12.5343 19.9586 11.0918 21.7316 11.0918H44.3861C46.1596 11.0918 47.6021 12.5348 47.6021 14.3078V31.1123C47.6021 32.8858 46.1591 34.3283 44.3861 34.3283H37.1636L34.3291 37.5098C34.1826 37.6738 33.9736 37.7678 33.7541 37.7678ZM21.7316 12.6318C20.8071 12.6318 20.0556 13.3838 20.0556 14.3078V31.1123C20.0556 32.0368 20.8076 32.7883 21.7316 32.7883H30.7846C31.0086 32.7883 31.2211 32.8858 31.3676 33.0553L33.7631 35.8303L36.2436 33.0463C36.3896 32.8823 36.5986 32.7883 36.8186 32.7883H44.3866C45.3111 32.7883 46.0626 32.0363 46.0626 31.1123V14.3078C46.0626 13.3833 45.3106 12.6318 44.3866 12.6318H21.7316Z" fill="#262262"/>
                                                                <path d="M38.893 21.6113H27.227C26.8015 21.6113 26.457 21.2668 26.457 20.8413C26.457 20.4158 26.8015 20.0713 27.227 20.0713H38.893C39.3185 20.0713 39.663 20.4158 39.663 20.8413C39.663 21.2668 39.318 21.6113 38.893 21.6113Z" fill="#262262"/>
                                                                <path d="M36.8029 25.6816H29.3169C28.8914 25.6816 28.5469 25.3371 28.5469 24.9116C28.5469 24.4861 28.8914 24.1416 29.3169 24.1416H36.8029C37.2284 24.1416 37.5729 24.4861 37.5729 24.9116C37.5729 25.3371 37.2279 25.6816 36.8029 25.6816Z" fill="#262262"/>
                                                                <path d="M16.8688 19.9365C10.5833 19.9365 5.48828 25.032 5.48828 31.317C5.48828 33.695 6.21828 35.902 7.46528 37.728L6.39828 40.326L9.11728 39.648C11.1493 41.5395 13.8738 42.6975 16.8693 42.6975C23.1548 42.6975 28.2498 37.602 28.2498 31.317C28.2498 25.032 23.1543 19.9365 16.8688 19.9365Z" fill="white"/>
                                                                <path d="M16.8694 43.4127C13.9324 43.4127 11.1254 42.3577 8.92144 40.4342L6.57144 41.0202C6.30994 41.0852 6.03394 40.9982 5.85744 40.7942C5.68094 40.5902 5.63444 40.3042 5.73694 40.0552L6.66044 37.8072C5.42494 35.8677 4.77344 33.6327 4.77344 31.3182C4.77344 24.6487 10.1994 19.2227 16.8689 19.2227C23.5384 19.2227 28.9644 24.6487 28.9644 31.3182C28.9649 37.9867 23.5389 43.4127 16.8694 43.4127ZM9.11744 38.9332C9.29594 38.9332 9.47094 39.0002 9.60444 39.1247C11.5844 40.9677 14.1644 41.9827 16.8694 41.9827C22.7504 41.9827 27.5349 37.1982 27.5349 31.3172C27.5349 25.4362 22.7504 20.6517 16.8694 20.6517C10.9884 20.6517 6.20394 25.4362 6.20394 31.3172C6.20394 33.4737 6.84444 35.5507 8.05644 37.3252C8.19244 37.5237 8.21894 37.7777 8.12744 38.0002L7.59744 39.2907L8.94494 38.9547C9.00194 38.9402 9.05994 38.9332 9.11744 38.9332Z" fill="#262262"/>
                                                                <path d="M18.9716 22.6561C23.2728 22.6561 26.7596 19.1693 26.7596 14.8681C26.7596 10.5669 23.2728 7.08008 18.9716 7.08008C14.6704 7.08008 11.1836 10.5669 11.1836 14.8681C11.1836 19.1693 14.6704 22.6561 18.9716 22.6561Z" fill="#FFD15C"/>
                                                                <path d="M19.2849 18.1584C18.8594 18.1584 18.5149 17.8139 18.5149 17.3884V12.8574H18.0669C17.6414 12.8574 17.2969 12.5129 17.2969 12.0874C17.2969 11.6619 17.6414 11.3174 18.0669 11.3174H19.2849C19.7104 11.3174 20.0549 11.6619 20.0549 12.0874V17.3884C20.0549 17.8134 19.7099 18.1584 19.2849 18.1584Z" fill="#262262"/>
                                                                <path d="M11.7041 32.4921C12.4855 32.4921 13.1191 31.8586 13.1191 31.0771C13.1191 30.2956 12.4855 29.6621 11.7041 29.6621C10.9226 29.6621 10.2891 30.2956 10.2891 31.0771C10.2891 31.8586 10.9226 32.4921 11.7041 32.4921Z" fill="#FFD15C"/>
                                                                <path d="M17.2041 32.4921C17.9855 32.4921 18.6191 31.8586 18.6191 31.0771C18.6191 30.2956 17.9855 29.6621 17.2041 29.6621C16.4226 29.6621 15.7891 30.2956 15.7891 31.0771C15.7891 31.8586 16.4226 32.4921 17.2041 32.4921Z" fill="#FFD15C"/>
                                                                <path d="M22.7041 32.4921C23.4855 32.4921 24.1191 31.8586 24.1191 31.0771C24.1191 30.2956 23.4855 29.6621 22.7041 29.6621C21.9226 29.6621 21.2891 30.2956 21.2891 31.0771C21.2891 31.8586 21.9226 32.4921 22.7041 32.4921Z" fill="#FFD15C"/>
                                                            </svg>

                                                        </span> Gift <strong>{starterFeatures.get('influencers.accept.limit')} creators</strong> per campaign</Li>
                                                    <Li><span>
                                                            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M18.9736 29.9834H8.05859V45.8059H18.9736V29.9834Z" fill="white"/>
                                                                <path d="M31.0252 16.1406H18.9727V45.8061H31.0252V16.1406Z" fill="white"/>
                                                                <path d="M41.9423 23.2402H31.0273V45.8057H41.9423V23.2402Z" fill="white"/>
                                                                <path d="M25.4652 10.3302L23.3097 11.2432C23.0402 11.3572 22.7457 11.1437 22.7712 10.8517L22.9737 8.51971L21.4392 6.75221C21.2472 6.53121 21.3597 6.18521 21.6452 6.11921L23.9257 5.59121L25.1327 3.58571C25.2837 3.33471 25.6477 3.33471 25.7987 3.58571L27.0057 5.59121L29.2862 6.11921C29.5717 6.18521 29.6837 6.53121 29.4922 6.75221L27.9577 8.51971L28.1602 10.8517C28.1857 11.1437 27.8912 11.3572 27.6217 11.2432L25.4652 10.3302Z" fill="#EFC5E6"/>
                                                                <path d="M25.7036 35.2238C25.2636 35.2238 24.9071 34.8673 24.9071 34.4273V29.0813H24.2926C23.8526 29.0813 23.4961 28.7248 23.4961 28.2848C23.4961 27.8448 23.8526 27.4883 24.2926 27.4883H25.7036C26.1436 27.4883 26.5001 27.8448 26.5001 28.2848V34.4273C26.5001 34.8668 26.1436 35.2238 25.7036 35.2238Z" fill="#262262"/>
                                                                <path d="M18.9736 29.9834H8.05859V35.6134H18.9736V29.9834Z" fill="#EFC5E6"/>
                                                                <path d="M30.7517 16.1406H18.6992V21.7706H30.7517V16.1406Z" fill="#EFC5E6"/>
                                                                <path d="M42.2861 23.2402H31.3711V28.8702H42.2861V23.2402Z" fill="#EFC5E6"/>
                                                                <path d="M18.9732 46.6025H8.05822C7.61822 46.6025 7.26172 46.246 7.26172 45.806V29.983C7.26172 29.543 7.61822 29.1865 8.05822 29.1865H18.9732C19.4132 29.1865 19.7697 29.543 19.7697 29.983V45.8055C19.7697 46.2455 19.4132 46.6025 18.9732 46.6025ZM8.85522 45.009H18.1767V30.7795H8.85522V45.009Z" fill="#262262"/>
                                                                <path d="M31.0253 46.6027H18.9723C18.5323 46.6027 18.1758 46.2462 18.1758 45.8062V16.1402C18.1758 15.7002 18.5323 15.3438 18.9723 15.3438H31.0248C31.4648 15.3438 31.8213 15.7002 31.8213 16.1402V45.8057C31.8218 46.2457 31.4653 46.6027 31.0253 46.6027ZM19.7688 45.0092H30.2288V16.9367H19.7688V45.0092Z" fill="#262262"/>
                                                                <path d="M41.942 46.6028H31.027C30.587 46.6028 30.2305 46.2463 30.2305 45.8063V23.2408C30.2305 22.8008 30.587 22.4443 31.027 22.4443H41.942C42.382 22.4443 42.7385 22.8008 42.7385 23.2408V45.8063C42.7385 46.2458 42.382 46.6028 41.942 46.6028ZM31.824 45.0093H41.1455V24.0373H31.824V45.0093Z" fill="#262262"/>
                                                            </svg>
                                                        </span> Match with up to <strong>{starterFeatures.get('influencers.matches.limit')} creators</strong></Li>
                                                </ul>
                                            ) : (
                                                <ul style={{listStyle: 'none'}}>
                                                    <Li><span>
                                                        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M31.5161 5.71048C39.0446 5.77498 45.2076 11.9045 45.3111 19.4325C45.3521 22.415 44.4541 25.1855 42.8946 27.468L43.9861 30.1255C44.0746 30.341 43.8806 30.566 43.6541 30.5095L40.8746 29.8165C38.3801 32.1385 35.0326 33.5565 31.3536 33.546C23.8716 33.5245 17.5416 27.2325 17.4771 19.751C17.4096 11.9675 23.7326 5.64398 31.5161 5.71048Z" fill="#FF855C"/>
                                                            <path d="M20.3648 13.4385C11.9073 13.4385 5.05078 20.2945 5.05078 28.7525C5.05078 31.9525 6.03278 34.9225 7.71078 37.3795L6.27478 40.8755L9.93328 39.9635C12.6678 42.509 16.3338 44.067 20.3648 44.067C28.8228 44.067 35.6788 37.2105 35.6788 28.753C35.6788 20.2955 28.8228 13.4385 20.3648 13.4385Z" fill="white"/>
                                                            <path d="M20.3649 44.7816C16.4299 44.7816 12.6709 43.3526 9.73594 40.7496L6.44794 41.5691C6.18644 41.6346 5.91044 41.5471 5.73394 41.3431C5.55744 41.1391 5.51094 40.8536 5.61344 40.6041L6.90494 37.4596C5.22294 34.8646 4.33594 31.8626 4.33594 28.7526C4.33594 19.9141 11.5264 12.7236 20.3649 12.7236C29.2034 12.7236 36.3939 19.9141 36.3939 28.7526C36.3944 37.5911 29.2039 44.7816 20.3649 44.7816ZM9.93394 39.2481C10.1124 39.2481 10.2874 39.3151 10.4209 39.4396C13.1309 41.9621 16.6624 43.3516 20.3649 43.3516C28.4149 43.3516 34.9639 36.8026 34.9639 28.7526C34.9639 20.7026 28.4149 14.1536 20.3649 14.1536C12.3149 14.1536 5.76594 20.7026 5.76594 28.7526C5.76594 31.7041 6.64294 34.5476 8.30144 36.9761C8.43744 37.1746 8.46394 37.4286 8.37244 37.6511L7.47344 39.8396L9.76094 39.2696C9.81794 39.2551 9.87594 39.2481 9.93394 39.2481Z" fill="#262262"/>
                                                            <path d="M14.9927 32.3893C14.5672 32.3893 14.2227 32.0448 14.2227 31.6193V25.9233C14.2227 25.4978 14.5672 25.1533 14.9927 25.1533C15.4182 25.1533 15.7627 25.4978 15.7627 25.9233V31.6193C15.7627 32.0448 15.4182 32.3893 14.9927 32.3893Z" fill="#262262"/>
                                                            <path d="M18.7192 32.3893C18.2937 32.3893 17.9492 32.0448 17.9492 31.6193V25.9233C17.9492 25.4978 18.2937 25.1533 18.7192 25.1533C19.1447 25.1533 19.4892 25.4978 19.4892 25.9233V31.6193C19.4892 32.0448 19.1442 32.3893 18.7192 32.3893Z" fill="#262262"/>
                                                            <path d="M21.6411 32.3893C21.2156 32.3893 20.8711 32.0448 20.8711 31.6193V25.9233C20.8711 25.4978 21.2156 25.1533 21.6411 25.1533C22.0666 25.1533 22.4111 25.4978 22.4111 25.9233V31.6193C22.4111 32.0448 22.0661 32.3893 21.6411 32.3893Z" fill="#262262"/>
                                                            <path d="M24.7856 29.9403C24.3601 29.9403 24.0156 29.5958 24.0156 29.1703V25.9233C24.0156 25.4978 24.3601 25.1533 24.7856 25.1533C25.2111 25.1533 25.5556 25.4978 25.5556 25.9233V29.1703C25.5556 29.5958 25.2106 29.9403 24.7856 29.9403Z" fill="#262262"/>
                                                            <path d="M24.7856 32.3537C24.3601 32.3537 24.0156 32.0092 24.0156 31.5837V31.5727C24.0156 31.1472 24.3601 30.8027 24.7856 30.8027C25.2111 30.8027 25.5556 31.1472 25.5556 31.5727V31.5837C25.5556 32.0092 25.2106 32.3537 24.7856 32.3537Z" fill="#262262"/>
                                                            <path d="M18.7172 29.4267H14.9927C14.5672 29.4267 14.2227 29.0822 14.2227 28.6567C14.2227 28.2312 14.5672 27.8867 14.9927 27.8867H18.7172C19.1427 27.8867 19.4872 28.2312 19.4872 28.6567C19.4872 29.0822 19.1422 29.4267 18.7172 29.4267Z" fill="#262262"/>
                                                        </svg>
                                                    </span> Create <strong>{isGrow ? `${growFeatures.get('gifts.create.limit')} campaigns` : 'Unlimited campaigns'}</strong> per month</Li>
                                                    <Li><span>
                                                            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M44.3862 11.8613H21.7312C20.3802 11.8613 19.2852 12.9563 19.2852 14.3073V31.1118C19.2852 32.4628 20.3802 33.5578 21.7312 33.5578H30.7842L33.7537 36.9973L36.8182 33.5578H44.3862C45.7372 33.5578 46.8322 32.4628 46.8322 31.1118V14.3073C46.8327 12.9563 45.7372 11.8613 44.3862 11.8613Z" fill="white"/>
                                                                <path d="M33.7541 37.7678C33.7521 37.7678 33.7501 37.7678 33.7481 37.7678C33.5266 37.7663 33.3161 37.6688 33.1711 37.5008L30.4321 34.3283H21.7316C19.9581 34.3283 18.5156 32.8853 18.5156 31.1123V14.3078C18.5156 12.5343 19.9586 11.0918 21.7316 11.0918H44.3861C46.1596 11.0918 47.6021 12.5348 47.6021 14.3078V31.1123C47.6021 32.8858 46.1591 34.3283 44.3861 34.3283H37.1636L34.3291 37.5098C34.1826 37.6738 33.9736 37.7678 33.7541 37.7678ZM21.7316 12.6318C20.8071 12.6318 20.0556 13.3838 20.0556 14.3078V31.1123C20.0556 32.0368 20.8076 32.7883 21.7316 32.7883H30.7846C31.0086 32.7883 31.2211 32.8858 31.3676 33.0553L33.7631 35.8303L36.2436 33.0463C36.3896 32.8823 36.5986 32.7883 36.8186 32.7883H44.3866C45.3111 32.7883 46.0626 32.0363 46.0626 31.1123V14.3078C46.0626 13.3833 45.3106 12.6318 44.3866 12.6318H21.7316Z" fill="#262262"/>
                                                                <path d="M38.893 21.6113H27.227C26.8015 21.6113 26.457 21.2668 26.457 20.8413C26.457 20.4158 26.8015 20.0713 27.227 20.0713H38.893C39.3185 20.0713 39.663 20.4158 39.663 20.8413C39.663 21.2668 39.318 21.6113 38.893 21.6113Z" fill="#262262"/>
                                                                <path d="M36.8029 25.6816H29.3169C28.8914 25.6816 28.5469 25.3371 28.5469 24.9116C28.5469 24.4861 28.8914 24.1416 29.3169 24.1416H36.8029C37.2284 24.1416 37.5729 24.4861 37.5729 24.9116C37.5729 25.3371 37.2279 25.6816 36.8029 25.6816Z" fill="#262262"/>
                                                                <path d="M16.8688 19.9365C10.5833 19.9365 5.48828 25.032 5.48828 31.317C5.48828 33.695 6.21828 35.902 7.46528 37.728L6.39828 40.326L9.11728 39.648C11.1493 41.5395 13.8738 42.6975 16.8693 42.6975C23.1548 42.6975 28.2498 37.602 28.2498 31.317C28.2498 25.032 23.1543 19.9365 16.8688 19.9365Z" fill="white"/>
                                                                <path d="M16.8694 43.4127C13.9324 43.4127 11.1254 42.3577 8.92144 40.4342L6.57144 41.0202C6.30994 41.0852 6.03394 40.9982 5.85744 40.7942C5.68094 40.5902 5.63444 40.3042 5.73694 40.0552L6.66044 37.8072C5.42494 35.8677 4.77344 33.6327 4.77344 31.3182C4.77344 24.6487 10.1994 19.2227 16.8689 19.2227C23.5384 19.2227 28.9644 24.6487 28.9644 31.3182C28.9649 37.9867 23.5389 43.4127 16.8694 43.4127ZM9.11744 38.9332C9.29594 38.9332 9.47094 39.0002 9.60444 39.1247C11.5844 40.9677 14.1644 41.9827 16.8694 41.9827C22.7504 41.9827 27.5349 37.1982 27.5349 31.3172C27.5349 25.4362 22.7504 20.6517 16.8694 20.6517C10.9884 20.6517 6.20394 25.4362 6.20394 31.3172C6.20394 33.4737 6.84444 35.5507 8.05644 37.3252C8.19244 37.5237 8.21894 37.7777 8.12744 38.0002L7.59744 39.2907L8.94494 38.9547C9.00194 38.9402 9.05994 38.9332 9.11744 38.9332Z" fill="#262262"/>
                                                                <path d="M18.9716 22.6561C23.2728 22.6561 26.7596 19.1693 26.7596 14.8681C26.7596 10.5669 23.2728 7.08008 18.9716 7.08008C14.6704 7.08008 11.1836 10.5669 11.1836 14.8681C11.1836 19.1693 14.6704 22.6561 18.9716 22.6561Z" fill="#FFD15C"/>
                                                                <path d="M19.2849 18.1584C18.8594 18.1584 18.5149 17.8139 18.5149 17.3884V12.8574H18.0669C17.6414 12.8574 17.2969 12.5129 17.2969 12.0874C17.2969 11.6619 17.6414 11.3174 18.0669 11.3174H19.2849C19.7104 11.3174 20.0549 11.6619 20.0549 12.0874V17.3884C20.0549 17.8134 19.7099 18.1584 19.2849 18.1584Z" fill="#262262"/>
                                                                <path d="M11.7041 32.4921C12.4855 32.4921 13.1191 31.8586 13.1191 31.0771C13.1191 30.2956 12.4855 29.6621 11.7041 29.6621C10.9226 29.6621 10.2891 30.2956 10.2891 31.0771C10.2891 31.8586 10.9226 32.4921 11.7041 32.4921Z" fill="#FFD15C"/>
                                                                <path d="M17.2041 32.4921C17.9855 32.4921 18.6191 31.8586 18.6191 31.0771C18.6191 30.2956 17.9855 29.6621 17.2041 29.6621C16.4226 29.6621 15.7891 30.2956 15.7891 31.0771C15.7891 31.8586 16.4226 32.4921 17.2041 32.4921Z" fill="#FFD15C"/>
                                                                <path d="M22.7041 32.4921C23.4855 32.4921 24.1191 31.8586 24.1191 31.0771C24.1191 30.2956 23.4855 29.6621 22.7041 29.6621C21.9226 29.6621 21.2891 30.2956 21.2891 31.0771C21.2891 31.8586 21.9226 32.4921 22.7041 32.4921Z" fill="#FFD15C"/>
                                                            </svg>

                                                        </span> Gift <strong>{isGrow ? `${growFeatures.get('influencers.accept.limit')} creators` : 'Unlimited creators'}</strong> per campaign</Li>
                                                    <Li><span>
                                                            <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M18.9736 29.9834H8.05859V45.8059H18.9736V29.9834Z" fill="white"/>
                                                                <path d="M31.0252 16.1406H18.9727V45.8061H31.0252V16.1406Z" fill="white"/>
                                                                <path d="M41.9423 23.2402H31.0273V45.8057H41.9423V23.2402Z" fill="white"/>
                                                                <path d="M25.4652 10.3302L23.3097 11.2432C23.0402 11.3572 22.7457 11.1437 22.7712 10.8517L22.9737 8.51971L21.4392 6.75221C21.2472 6.53121 21.3597 6.18521 21.6452 6.11921L23.9257 5.59121L25.1327 3.58571C25.2837 3.33471 25.6477 3.33471 25.7987 3.58571L27.0057 5.59121L29.2862 6.11921C29.5717 6.18521 29.6837 6.53121 29.4922 6.75221L27.9577 8.51971L28.1602 10.8517C28.1857 11.1437 27.8912 11.3572 27.6217 11.2432L25.4652 10.3302Z" fill="#EFC5E6"/>
                                                                <path d="M25.7036 35.2238C25.2636 35.2238 24.9071 34.8673 24.9071 34.4273V29.0813H24.2926C23.8526 29.0813 23.4961 28.7248 23.4961 28.2848C23.4961 27.8448 23.8526 27.4883 24.2926 27.4883H25.7036C26.1436 27.4883 26.5001 27.8448 26.5001 28.2848V34.4273C26.5001 34.8668 26.1436 35.2238 25.7036 35.2238Z" fill="#262262"/>
                                                                <path d="M18.9736 29.9834H8.05859V35.6134H18.9736V29.9834Z" fill="#EFC5E6"/>
                                                                <path d="M30.7517 16.1406H18.6992V21.7706H30.7517V16.1406Z" fill="#EFC5E6"/>
                                                                <path d="M42.2861 23.2402H31.3711V28.8702H42.2861V23.2402Z" fill="#EFC5E6"/>
                                                                <path d="M18.9732 46.6025H8.05822C7.61822 46.6025 7.26172 46.246 7.26172 45.806V29.983C7.26172 29.543 7.61822 29.1865 8.05822 29.1865H18.9732C19.4132 29.1865 19.7697 29.543 19.7697 29.983V45.8055C19.7697 46.2455 19.4132 46.6025 18.9732 46.6025ZM8.85522 45.009H18.1767V30.7795H8.85522V45.009Z" fill="#262262"/>
                                                                <path d="M31.0253 46.6027H18.9723C18.5323 46.6027 18.1758 46.2462 18.1758 45.8062V16.1402C18.1758 15.7002 18.5323 15.3438 18.9723 15.3438H31.0248C31.4648 15.3438 31.8213 15.7002 31.8213 16.1402V45.8057C31.8218 46.2457 31.4653 46.6027 31.0253 46.6027ZM19.7688 45.0092H30.2288V16.9367H19.7688V45.0092Z" fill="#262262"/>
                                                                <path d="M41.942 46.6028H31.027C30.587 46.6028 30.2305 46.2463 30.2305 45.8063V23.2408C30.2305 22.8008 30.587 22.4443 31.027 22.4443H41.942C42.382 22.4443 42.7385 22.8008 42.7385 23.2408V45.8063C42.7385 46.2458 42.382 46.6028 41.942 46.6028ZM31.824 45.0093H41.1455V24.0373H31.824V45.0093Z" fill="#262262"/>
                                                            </svg>
                                                        </span> Match with up to <strong>{isGrow ? `${growFeatures.get('influencers.matches.limit')} creators` : 'Unlimited creators'}</strong></Li>
                                                    <Li>
                                                        <span>
                                                        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M39.2628 16.7109H12.1948C10.6998 16.7109 9.48828 17.9229 9.48828 19.4174V38.0119C9.48828 39.5069 10.7003 40.7184 12.1948 40.7184H39.2628C40.7578 40.7184 41.9693 39.5064 41.9693 38.0119V19.4174C41.9698 17.9224 40.7578 16.7109 39.2628 16.7109Z" fill="white"/>
                                                            <path d="M39.2633 41.4894H12.1953C10.2783 41.4894 8.71875 39.9299 8.71875 38.0129V19.4179C8.71875 17.5009 10.2783 15.9414 12.1953 15.9414H39.2633C41.1803 15.9414 42.7397 17.5009 42.7397 19.4179V38.0124C42.7402 39.9294 41.1803 41.4894 39.2633 41.4894ZM12.1953 17.4814C11.1273 17.4814 10.2588 18.3504 10.2588 19.4179V38.0124C10.2588 39.0804 11.1278 39.9489 12.1953 39.9489H39.2633C40.3313 39.9489 41.1997 39.0799 41.1997 38.0124V19.4179C41.1997 18.3499 40.3308 17.4814 39.2633 17.4814H12.1953Z" fill="#262262"/>
                                                            <path d="M35.3643 12.9111H8.29634C6.80134 12.9111 5.58984 14.1231 5.58984 15.6176V34.2121C5.58984 35.7071 6.80184 36.9186 8.29634 36.9186H35.3643C36.8593 36.9186 38.0708 35.7066 38.0708 34.2121V15.6176C38.0713 14.1226 36.8593 12.9111 35.3643 12.9111Z" fill="white"/>
                                                            <path d="M35.3648 37.6896H8.29681C6.37981 37.6896 4.82031 36.1301 4.82031 34.2131V15.6181C4.82031 13.7011 6.37981 12.1416 8.29681 12.1416H35.3648C37.2818 12.1416 38.8413 13.7011 38.8413 15.6181V34.2126C38.8418 36.1296 37.2818 37.6896 35.3648 37.6896ZM8.29681 13.6816C7.22881 13.6816 6.36031 14.5506 6.36031 15.6181V34.2126C6.36031 35.2806 7.22931 36.1491 8.29681 36.1491H35.3648C36.4328 36.1491 37.3013 35.2801 37.3013 34.2126V15.6181C37.3013 14.5501 36.4323 13.6816 35.3648 13.6816H8.29681Z" fill="#262262"/>
                                                            <path d="M40.0156 22.0039C43.6055 22.0039 46.5156 19.0938 46.5156 15.5039C46.5156 11.9141 43.6055 9.00391 40.0156 9.00391C36.4258 9.00391 33.5156 11.9141 33.5156 15.5039C33.5156 19.0938 36.4258 22.0039 40.0156 22.0039Z" fill="#EFC5E6"/>
                                                            <path d="M39.5659 17.8125C39.3729 17.8125 39.1879 17.7345 39.0529 17.5955L37.3934 15.8865C37.1184 15.603 37.1249 15.1505 37.4084 14.8755C37.6919 14.6005 38.1444 14.6075 38.4194 14.8905L39.5154 16.0195L41.5594 13.4795C41.8069 13.172 42.2569 13.1235 42.5649 13.3705C42.8724 13.618 42.9214 14.068 42.6739 14.376L40.1229 17.546C39.9944 17.7055 39.8039 17.8025 39.5989 17.812C39.5879 17.8125 39.5769 17.8125 39.5659 17.8125Z" fill="white"/>
                                                            <path d="M19.026 23.6057C20.352 23.6057 21.427 22.5307 21.427 21.2047C21.427 19.8787 20.352 18.8037 19.026 18.8037C17.7 18.8037 16.625 19.8787 16.625 21.2047C16.625 22.5307 17.7 23.6057 19.026 23.6057Z" fill="#EFC5E6"/>
                                                            <path d="M9.82031 32.2269H34.0813L25.3188 23.4639L19.5828 29.1999L15.6983 25.3154L9.82031 32.2269Z" fill="#EFC5E6"/>
                                                        </svg>

                                                        </span> Capture Instagram, TikTok, and YouTube posts plus Instagram stories
                                                    </Li>
                                                </ul>
                                            )}
                                        </Offers>
                                    </Group>
                                </Details>
                            </Body>

                            <Info>
                                <span>
                                    {!loadingSubscription && data?.status !== "canceled" &&
                                        `Purchase date: ${Helpers.formatDate(data?.subscribed_at || data?.current_period_start || '', 'MMMM dd, Y')}`}
                                        
                                </span>


                                <span>
                                    {!loadingSubscription && !data?.paused_at && data?.status !== "canceled" &&
                                        `${data?.canceled_at ? 'Expiry':'Next billing'} date: ${Helpers.formatDate(data?.expires_at || data?.current_period_end || '', 'MMMM dd, Y')}`}
                                </span>
                            </Info>

                            {data?.paused_at ? (
                                <Info>
                                    <span>
                                        {data?.paused_at ? `After ${Helpers.formatDate(data?.expires_at || '', 'MMMM dd, Y')} your subscription will be paused for one month.` : null}
                                    </span>
                                </Info>
                            ) : null}
                        </>
                    ) : (
                        <NoResult
                            image={'pig'}
                            title={'You are not subscribed to a plan'}
                        />
                    )
                }

                <BtnActions>
                    {(hasSubscription && hasActiveSubscription) && data?.status !== "canceled" && data?.status !== "incomplete" && (
                        <>
                            <StyledButton
                                size={'medium'}
                                theme={'danger'}
                                disabled={!hasSubscription || loadingSubscription}
                                onClick={() => toggleCancelModal()}
                            >
                                Cancel Plan
                            </StyledButton>

                            {/* {data?.status !== 'paused' && !data?.paused_at ? (
                                // <StyledButton
                                //     size={'medium'}
                                //     theme={'outline'}
                                //     disabled={!hasSubscription || loadingSubscription}
                                //     onClick={() => togglePauseModal()}
                                // >
                                //     Pause Plan
                                // </StyledButton>
                            ) : (
                                <StyledButton
                                size={'medium'}
                                theme={'outline'}
                                disabled={!hasSubscription || loadingSubscription}
                                onClick={() => resumeSubscription()}
                            >
                                Resume Plan
                            </StyledButton>
                            )} */}
                        </>
                    )}

                    <StyledButton
                        theme={'blue'}
                        size={'medium'}
                        disabled={!hasSubscription || loadingSubscription}
                        onClick={onChangePlan}
                    >
                        {data?.canceled_at || (data?.status === 'paused' || data?.status === "canceled" || data?.status === "incomplete") ? 'Resubscribe' : 'Change plan'}
                    </StyledButton>

                    {hasSubscription && (
                        <StyledButton
                            theme={'blue'}
                            size={'medium'}
                            disabled={loadingSubscription}
                            onClick={onChangeCard}
                        >
                            Change Card
                        </StyledButton>
                    )}
                </BtnActions>
            </Container>

            {showPauseModal && (
                <ModalBackdrop onClick={() => togglePauseModal()}>
                    <Modal onClick={(e) => e.stopPropagation()}>
                        <ModalContent>
                            <h1>Are you sure you want to Pause your plan for 1 month?</h1>
                        </ModalContent>

                        <ModalDesc>
                            <p>After {Helpers.formatDate(data?.expires_at || '', 'MMMM dd, Y')} your subscription will be paused for one month, and will recommence on {' '}
                            {/* this looks confusing, but basically it just adds a month and formats it */}
                            {new Date(new Date(Helpers.formatDate(data?.expires_at || '', 'MMMM dd, Y')).setMonth(new Date(Helpers.formatDate(data?.expires_at || '', 'MMMM dd, Y')).getMonth() + 1)).toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}.</p>
                        </ModalDesc>

                        <ModalActions>
                            <ModalButton theme="outline" onClick={() => togglePauseModal()}>No</ModalButton>
                            <ModalButton onClick={() => {
                                pauseSubscription();
                            }}>Yes</ModalButton>
                        </ModalActions>
                    </Modal>
                </ModalBackdrop>

            )}

            {onCancelPlan && showCancelModal && (
                <CancelModal show={showCancelModal} brand={brand} onClose={() => toggleCancelModal()} onSubmit={(form) => onCancelPlan(form)} endDate={Helpers.formatDate(data?.expires_at || '', 'MMMM dd, Y')} subtext={`You will continue to be able to use Gifted until ${Helpers.formatDate(data?.expires_at || '', 'MMMM dd, Y')} when your current plan expires.`} />
            )}

        </>
    );
}


const ModalBackdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    background-color: rgba(38,33,97,0.5);
    backdrop-filter: blur(8px);
`
const Modal = styled.div`
    width: 537px;
    background-color: var(--background-alt);
    padding: 24px;
    border-radius: 32px;
    box-shadow: var(--shadow-1);
`
const ModalContent = styled.div`
    font-weight: bold;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    margin: 8px 0;
    letter-spacing: -0.75px;
    color: var(--grey-9);
    h1 {
        font-size: 40px;
        line-height: 48px;
        text-align: center;
        letter-spacing: -1.25px;
        color: #262161;
        font-weight: 400;
    }
`
const ModalDesc = styled.div`
    margin-top: 24px;
    p {
        font-weight: 700;
        font-size: 18px;
        line-height: 24px;
        text-align: center;
        letter-spacing: -0.25px;
        color: #9299BE;
        font-weight: 400;
    }
`
const ModalActions = styled.div`
    margin-top: 50px;
    display: flex;
    justify-content: space-between;
`
const ModalButton = styled(Button)`
    width: 47%;
`


const Value = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    span {
        &:first-child {
            font-size: 24px;
            font-weight: 300;
            line-height: 32px;
            letter-spacing: -0.75px;
            color: var(--grey-9);
        }

        &:last-child {
            font-size: 12px;
            font-weight: 600;
            line-height: 16px;
            margin-top: -1px;
            color: var(--grey-6);
        }
    }
`;

const Title = styled.span`
    font-size: 20px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: -0.5px;
    color: var(--grey-9);
`;

const Header = styled.div`
    flex: 0 0 120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 0 16px;
`;

const Divider = styled.div`
    width: 90%;
    height: 1px;
    margin: 0 auto;
    background-color: var(--grey-3);
`;

const GroupTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    line-height: 12px;
    margin: auto;
    letter-spacing: 2px;
    text-align: center;
    text-transform: uppercase;
    color: var(--grey-6);

    @media (max-width:900px) and (min-width:0px) {
        display: none;
    }
`;

const Offers = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    span {
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
    }


`;

const Group = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 8px;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 16px;
    padding: 16px 0;
`;

const Info = styled.div`
    display: flex;
    justify-content: space-between;
  
    @media (max-width:900px) and (min-width:0px) {
      flex-direction: column;
    }

    span {
        font-size: 14px;
        font-weight: 600;
        line-height: 24px;
        color: var(--grey-9);
    }
`;

const StyledButton = styled(Button)`
    width: 186px;
`;

const BtnActions = styled.div`
    align-self: center;
    display: flex;
    align-items: center;
    column-gap: 12px;
    margin-top: 24px;
    padding: 0 36px;

    @media (max-width:900px) and (min-width:0px) {
            
        button {
            width: 100px;
            font-size: 12px;
            padding: 0px 8px;
        }
    }
`;

const Body = styled.div`
    border: 1px solid var(--grey-3);
    border-radius: 16px;
    background-color: var(--grey-1);
`;

const StatusBadge = styled(Badge) <{
    $status?: SubscriptionStatus;
}>`
    align-items: center;
    align-self: center;
    font-size: 11px;
    box-shadow: var(--shadow-1);
    background-color: var(--background-alt);
    color: var(--grey-9);
    margin-bottom: 16px;
    cursor: default;

    ${(props) => (props.$status === 'active' || props.$status === 'trialing') && `
        background-color: var(--success-muted);
        color: var(--success-active);
    `}
    
    ${(props) => (props.$status === 'canceled') && `
        background-color: var(--warning-muted);
        color: var(--warning-active);
    `}
    
    ${(props) => (props.$status === 'unpaid' || props.$status === 'past_due' || props.$status === 'incomplete' || props.$status === 'incomplete_expired') && `
        background-color: var(--error-muted);
        color: var(--error-active);
    `}

    &.canceled {
        background-color: var(--warning-muted);
        color: var(--warning-active);
    }

`;

const Container = styled.div<{
    $loading?: boolean;
}>`
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    padding: 0 36px;

    ${(props) => props.$loading && `
        ${Title},
        ${Value} span,
        ${GroupTitle},
        ${Offers} span,
        ${Info} span,
        ${StyledButton} {
            border-radius: 4px;
            background: linear-gradient(
                to right,
                var(--grey-3),
                var(--grey-2) 50%,
                var(--grey-3) 80%
            ), var(--grey-3);
            background-position: 100% 0, 0 0;
            background-repeat: repeat-y;
            background-size: 200% 200%;
            color: transparent;
            animation: animate 1s infinite;

            &:before {
                content: '########';
                visibility: hidden;
            }
        }

        ${Title}:before,
        ${Info} span:before {
            content: '###############';
        }

        ${StyledButton} {
            border-radius: 16px;
        }
    `}
`;

const Li = styled.div`
    display: flex;
    align-items: center;
    height: 50px;

    svg {
        height: 50px;
    }
    span {
        margin-right: 8px;
    }

    strong {
        padding: 0px 4px;
        font-weight: 600;
    }

    @media (max-width:900px) and (min-width:0px) {
        display: none;
    }
`
